import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../assets/images/Logo.png';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const stores = [
  { id: 'Loughton', name: 'Loughton' },
  { id: 'Thames', name: 'Thames' },
  { id: 'Bitterne', name: 'Bitterne' },
  { id: 'Birkenhead', name: 'Birkenhead' },
  { id: 'Shrewsbury', name: 'Shrewsbury' },
  { id: 'FortWilliam', name: 'Fort William' },
  { id: 'EastGate', name: 'East Gate' },
  { id: 'Inverness', name: 'Inverness' },
  { id: 'Hanley', name: 'Hanley' },
  { id: 'Scunthrope', name: 'Scunthorpe' },
  { id: 'Goole', name: 'Goole' },
  { id: 'Liverpool', name: 'Liverpool' },
  { id: 'Oban', name: 'Oban' },
  { id: 'Leeds', name: 'Leeds' },
  { id: 'Worcester', name: 'Worcester' },
];

const navLinks = [
  { label: 'ABOUT US', to: '/about-us' },
  { label: 'SERVICES', to: '/services' },
  { label: 'CONTACT US', to: '/contact-us' },
];

function PhoneClinicNavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [storesOpen, setStoresOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        background: scrolled ? 'rgba(6,6,6,0.96)' : 'transparent',
        backdropFilter: scrolled ? 'blur(24px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(198,21,21,0.18)' : 'none',
        transition: 'background 0.4s ease, backdrop-filter 0.4s ease, border-color 0.4s ease',
        padding: '0 5%',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '78px' }}>

        {/* Logo */}
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <motion.img
            src={logo} alt="Phone Clinic"
            style={{ height: '68px' }}
            whileHover={{ scale: 1.04 }}
            transition={{ duration: 0.2 }}
          />
        </Link>

        {/* Desktop Nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>

          {navLinks.map((link, i) => (
            <motion.div
              key={link.label}
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * i + 0.3, duration: 0.4 }}
              className="d-none d-md-block"
              style={{ position: 'relative' }}
            >
              <Link to={link.to} style={{ textDecoration: 'none' }}>
                <motion.span
                  whileHover={{ color: '#c61515' }}
                  style={{
                    color: 'white', fontSize: '12px', letterSpacing: '1.8px',
                    fontWeight: '700', position: 'relative', display: 'inline-block',
                  }}
                >
                  {link.label}
                </motion.span>
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.25 }}
                  style={{
                    position: 'absolute', bottom: '-3px', left: 0, right: 0,
                    height: '2px', background: '#c61515', transformOrigin: 'left',
                  }}
                />
              </Link>
            </motion.div>
          ))}

          {/* Stores Dropdown */}
          <motion.div
            className="d-none d-md-block"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.4 }}
            style={{ position: 'relative' }}
            onMouseEnter={() => setStoresOpen(true)}
            onMouseLeave={() => setStoresOpen(false)}
          >
            <div style={{
              display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer',
            }}>
              <motion.span
                animate={{ color: storesOpen ? '#c61515' : 'white' }}
                transition={{ duration: 0.2 }}
                style={{ fontSize: '12px', letterSpacing: '1.8px', fontWeight: '700' }}
              >
                STORES
              </motion.span>
              <motion.div
                animate={{ rotate: storesOpen ? 180 : 0 }}
                transition={{ duration: 0.25 }}
              >
                <KeyboardArrowDownIcon style={{ fontSize: '18px', color: storesOpen ? '#c61515' : 'white' }} />
              </motion.div>
            </div>

            <AnimatePresence>
              {storesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 12, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.97 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    position: 'absolute', top: 'calc(100% + 14px)',
                    left: '50%', transform: 'translateX(-50%)',
                    background: 'rgba(10,10,10,0.98)',
                    backdropFilter: 'blur(24px)',
                    border: '1px solid rgba(198,21,21,0.28)',
                    borderRadius: '14px', padding: '20px 16px',
                    minWidth: '520px',
                    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2px',
                    boxShadow: '0 24px 80px rgba(0,0,0,0.85), 0 0 50px rgba(198,21,21,0.08)',
                  }}
                >
                  <div style={{
                    gridColumn: '1 / -1', fontSize: '10px', letterSpacing: '3px',
                    color: '#c61515', fontWeight: '800', marginBottom: '12px',
                    paddingBottom: '12px', borderBottom: '1px solid rgba(198,21,21,0.18)',
                  }}>
                    PHONE CLINIC LOCATIONS
                  </div>
                  {stores.map((store) => (
                    <Link key={store.id} to={`/stores/${store.id}`} style={{ textDecoration: 'none' }}>
                      <motion.div
                        whileHover={{ background: 'rgba(198,21,21,0.14)', color: '#c61515' }}
                        transition={{ duration: 0.15 }}
                        style={{
                          color: '#bbb', fontSize: '13px', padding: '9px 12px',
                          borderRadius: '8px', cursor: 'pointer',
                        }}
                      >
                        {store.name}
                      </motion.div>
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Book Repair CTA */}
          <motion.div
            className="d-none d-md-block"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7, duration: 0.4 }}
          >
            <Link to="/services" style={{ textDecoration: 'none' }}>
              <motion.button
                whileHover={{ scale: 1.06, boxShadow: '0 0 28px rgba(198,21,21,0.55)' }}
                whileTap={{ scale: 0.95 }}
                style={{
                  background: 'linear-gradient(135deg, #c61515, #780e0f)',
                  color: 'white', border: 'none',
                  padding: '10px 22px', borderRadius: '8px',
                  fontSize: '12px', letterSpacing: '1.5px', fontWeight: '700',
                  cursor: 'pointer', fontFamily: 'Catamaran, sans-serif',
                }}
              >
                BOOK REPAIR
              </motion.button>
            </Link>
          </motion.div>

          {/* Mobile Hamburger */}
          <motion.button
            className="d-md-none"
            onClick={() => setMobileOpen(!mobileOpen)}
            whileTap={{ scale: 0.88 }}
            style={{
              background: 'transparent',
              border: '1px solid rgba(198,21,21,0.45)',
              color: 'white', padding: '7px', borderRadius: '8px',
              cursor: 'pointer', display: 'flex', alignItems: 'center',
            }}
          >
            <AnimatePresence mode="wait">
              {mobileOpen
                ? <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}><CloseIcon /></motion.div>
                : <motion.div key="open"  initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}><MenuIcon /></motion.div>
              }
            </AnimatePresence>
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{
              overflow: 'hidden',
              background: 'rgba(6,6,6,0.98)',
              borderTop: '1px solid rgba(198,21,21,0.18)',
            }}
          >
            {[...navLinks, { label: 'STORES', to: '/stores/Hanley' }].map((link, i) => (
              <motion.div
                key={link.label}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i }}
              >
                <Link
                  to={link.to}
                  style={{
                    display: 'block', color: '#ccc', textDecoration: 'none',
                    padding: '14px 20px', fontSize: '13px', letterSpacing: '1.5px',
                    fontWeight: '700', borderBottom: '1px solid rgba(255,255,255,0.05)',
                  }}
                >
                  {link.label}
                </Link>
              </motion.div>
            ))}
            <div style={{ padding: '16px 20px' }}>
              <Link to="/services" style={{ textDecoration: 'none' }}>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #c61515, #780e0f)',
                    color: 'white', border: 'none', padding: '14px', borderRadius: '8px',
                    fontSize: '13px', letterSpacing: '1.5px', fontWeight: '700',
                    cursor: 'pointer', fontFamily: 'Catamaran, sans-serif',
                  }}
                >
                  BOOK REPAIR
                </motion.button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

export default PhoneClinicNavBar;
